import { apiFetch } from "./apiService";

export type ApiResult<T> =
  { success: true; data: T } | { success: false; statusCode: number; error: string };

// Raw shape returned by the CRM for the `fields` we request below -
// kept private, callers get the mapped ZohoRelatedMeter instead
interface ZohoRelatedMeterRaw {
  id: string;
  MPRN: string | null;
  Mpan_Top_1: string | null;
  Mpan_Bottom_1: string | null;
  Deal_Name: string | null;
}

export interface ZohoCustomerRaw {
  Type_of_Business: "Ltd" | "Sole Trader" | "Other" | "Charity" | "LLP" | "Registered society";
  Business_Address?: string;
  Company_Reg_Number?: string;
  First_Name: string;
  Last_Name: string;
  Phone?: string;
  Email: string;
  Account_Name: string;
}

// Raw meter record from the CRM (only the fields we map - the real record has
// ~300). Elec (_E1) and gas (_G1 / Gas_) variants live side by side on the same
// record; Which_Meter says which apply. Zoho sends null for any empty field.
// Types marked (?) were null in every sample seen so far - best guess
export interface ZohoMeterRaw {
  Which_Meter: string | null;
  MPRN: string | null;
  Mpan_Top_1: string | null;
  Mpan_Bottom_1: string | null;
  Street: string | null;
  Town: string | null;
  County: string | null;
  Country: string | null;
  Postcode: string | null;
  Amount: number | null; // (?)
  // Electricity
  New_Supplier_E1: string | null;
  Contract_Type: string | null;
  New_SC_E1: string | number | null; // (?) Current_SC_p_E1 comes back as a string
  CED_E1: string | null;
  New_CED_E1: string | null;
  AQ_1: number | null;
  Passthrough_1: string | null;
  Uplift_p_E1: number | null; // (?)
  SC_Uplift_E1_New: number | null; // (?)
  New_Day_Rate_E1: string | number | null; // (?) Current_Day_Rate_p_E1 comes back as a string
  New_Night_Rate_E1: string | number | null; // (?)
  New_Eve_Weekend_p: string | number | null; // (?)
  New_Evening_weekend_and_night_rate: string | number | null; // (?)
  New_KVA_Charge: number | null; // (?)
  KVA_Capacity: number | null;
  KVA_Charge_Frequency: string | null;
  // Gas
  New_Supplier_G1: string | null;
  Contract_Type_Gas: string | null;
  New_SC_p_G1: string | number | null; // (?)
  CED_G1: string | null;
  New_CED_G1: string | null;
  Gas_AQ_1: number | null;
  Gas_Passthrough: string | null;
  Uplift_p_G1: number | null; // (?)
  SC_Uplift_Gas: number | null; // (?)
  New_Unit_Price_p_G1: string | number | null; // (?)
}

export interface ZohoRelatedMeter {
  id: string;
  mprn: string | null;
  mpan: string | null;
  meterName: string | null;
}

// Use only bottom for unique id
const mapZohoRelatedMeter = (raw: ZohoRelatedMeterRaw): ZohoRelatedMeter => ({
  id: raw.id,
  mprn: raw.MPRN,
  mpan: raw.Mpan_Bottom_1?.slice(-14) ?? null,
  meterName: raw.Deal_Name,
});

const GET_RELATED_RECORDS_ROUTE =
  "/data-remote/records/related/{module}/{record_id}/{related_module}";

const buildRelatedRecordsRoute = (module: string, recordId: string, relatedModule: string) =>
  GET_RELATED_RECORDS_ROUTE.replace("{module}", module)
    .replace("{record_id}", recordId)
    .replace("{related_module}", relatedModule);

export const retrieveCustomerDetails = async (
  customerId: string,
): Promise<ApiResult<ZohoCustomerRaw>> => {
  const res = await apiFetch(`/data-remote/records/customer/${customerId}`);

  if (!res.ok) {
    return { success: false, statusCode: res.status, error: await res.text() };
  }

  const response = (await res.json()) as { data: ZohoCustomerRaw[] };
  return { success: true, data: response.data[0] };
};

export const retrieveMeterDetails = async (meterId: string): Promise<ApiResult<ZohoMeterRaw>> => {
  const res = await apiFetch(`/data-remote/records/meter/${meterId}`);

  if (!res.ok) {
    return { success: false, statusCode: res.status, error: await res.text() };
  }

  const response = (await res.json()) as { data: ZohoMeterRaw[] };
  return { success: true, data: response.data[0] };
};

export const retrieveRelatedMeters = async (
  customerId: string,
): Promise<ApiResult<ZohoRelatedMeter[]>> => {
  const path = buildRelatedRecordsRoute("customer", customerId, "meter");
  const params = new URLSearchParams({
    fields: "id,MPRN,Mpan_Top_1,Mpan_Bottom_1,Deal_Name",
  });
  const res = await apiFetch(`${path}?${params.toString()}`);

  if (!res.ok) {
    return { success: false, statusCode: res.status, error: await res.text() };
  }

  const response = (await res.json()) as { data: ZohoRelatedMeterRaw[] };
  const raw = response.data;
  return { success: true, data: raw.map(mapZohoRelatedMeter) };
};
